import { Injectable, effect, signal } from '@angular/core';
import { InMemoryCacheService } from '../cache/in-memory-cache.service';
import { OrgRole } from '../permissions/permission.types';
import { OrganizationMembersService } from 'src/app/pages/organization-settings/services/organization-members.service';
import { UtilsService } from '..';

@Injectable({
  providedIn: 'root'
})
export class OrgContextService {
  private _orgRole = signal<OrgRole>('Viewer');
  public readonly orgRole = this._orgRole.asReadonly();
  private readonly orgMembersLoadPromises = new Map<number, Promise<void>>();

  constructor(
    private readonly cache: InMemoryCacheService,
    private readonly orgMembersService: OrganizationMembersService,
    private utils: UtilsService
  ) {
    this.hydrateFromAuthRole();

    effect(() => {
      const membersData = this.orgMembersService.membersData();
      const userId = this.cache.auth?.user?.user?.id;
      const membersOrgId = this.orgMembersService.membersDataOrgId();
      const currentOrgId = this.resolveCurrentOrgId();
      if (!currentOrgId) {
        this.hydrateFromAuthRole();
        return;
      }

      if (!membersData?.members?.length || !userId) {
        this.hydrateFromAuthRole();
        return;
      }

      if (currentOrgId && membersOrgId !== currentOrgId) {
        return;
      }

      const member = membersData.members.find(item => item.userId === userId || item.membershipId === userId);
      if (!member?.role) {
        this.hydrateFromAuthRole();
        return;
      }

      this.setCurrentOrgRole(this.normalizeOrgRole(member.role));
    }, { allowSignalWrites: true });
  }

  public getCurrentOrgRole(): OrgRole {
    return this._orgRole();
  }

  public async ensureOrgContextReady(): Promise<void> {
    const orgId = await this.resolveCurrentOrgIdWithRetry();
    if (!orgId) {
      this.hydrateFromAuthRole();
      return;
    }

    const existingPromise = this.orgMembersLoadPromises.get(orgId);
    if (existingPromise) {
      return existingPromise;
    }

    const shouldLoadMembers =
      this.orgMembersService.membersDataOrgId() !== orgId ||
      !this.orgMembersService.membersData();

    const membersLoadPromise = (async () => {
      this.hydrateFromAuthRole();

      if (shouldLoadMembers) {
        try {
          await this.orgMembersService.loadMembersIfNeeded(orgId, true);
        } catch {
          // If members API fails, fallback to auth token role.
        }
      }

      if (!this.hydrateFromMembersForOrg(orgId)) {
        this.hydrateFromAuthRole();
      }
    })().finally(() => {
      this.orgMembersLoadPromises.delete(orgId);
    });

    this.orgMembersLoadPromises.set(orgId, membersLoadPromise);
    return membersLoadPromise;
  }

  public setCurrentOrgRole(role: OrgRole): void {
    this._orgRole.set(role);
  }

  private hydrateFromAuthRole(): void {
    const tokenRole = this.cache.auth?.user?.user?.role as any;
    const roleName = typeof tokenRole === 'string' ? tokenRole : tokenRole?.roleName;
    if (!roleName) {
      return;
    }

    this.setCurrentOrgRole(this.normalizeOrgRole(roleName));
  }

  private hydrateFromMembersForOrg(orgId: number): boolean {
    const membersData = this.orgMembersService.membersData();
    const membersDataOrgId = this.orgMembersService.membersDataOrgId();
    if (!membersData?.members?.length || membersDataOrgId !== orgId) {
      return false;
    }

    const userId = this.cache.auth?.user?.user?.id;
    if (!userId) {
      return false;
    }

    const member = membersData.members.find(item => item.userId === userId || item.membershipId === userId);
    if (!member?.role) {
      return false;
    }

    this.setCurrentOrgRole(this.normalizeOrgRole(member.role));
    return true;
  }

  private resolveCurrentOrgId(): number | null {
    const orgId = this.cache.auth?.organization?.id
      ?? this.cache.user?.currentOrganizationId
      ?? this.cache.auth?.user?.user?.currentOrganizationId;
    const numericOrgId = Number(orgId);
    if (!Number.isFinite(numericOrgId) || numericOrgId <= 0) {
      return null;
    }

    return numericOrgId;
  }

  private async resolveCurrentOrgIdWithRetry(): Promise<number | null> {
    const initialOrgId = this.resolveCurrentOrgId();
    if (initialOrgId) {
      return initialOrgId;
    }

    const startedAt = Date.now();
    const timeoutMs = 1500;
    const intervalMs = 50;

    while (Date.now() - startedAt < timeoutMs) {
      const resolvedOrgId = this.resolveCurrentOrgId();
      if (resolvedOrgId) {
        return resolvedOrgId;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return null;
  }

  private normalizeOrgRole(role: string): OrgRole {
    this.utils.logger.info('role on normalizeOrgRole: ', role);
    const normalized = (role ?? '').trim().toLowerCase();
    if (normalized === 'billingowner') return 'BillingOwner';
    if (normalized === 'owner') return 'Owner';
    if (normalized === 'admin') return 'Admin';
    if (normalized === 'member') return 'Member';
    if (normalized === 'viewer') return 'Viewer';
    return 'Viewer';
  }
}
